import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { FaDownload } from 'react-icons/fa';
import { GetCertificatesUserService } from '../../services/GetCertificatesUserService/GetCertificatesUserService';
import { useAuth } from '../../context/AuthContext';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

const CertificatesButtonComponent = ({ dataCourse, hasSentProgress}) => {
  const { token, idUser } = useAuth();
  const [isActive, setIsActive] = useState(false);

  const name = sessionStorage.getItem('name') ?? '';
  const lastName = sessionStorage.getItem('lastName') ?? '';
  const source = sessionStorage.getItem('source') ?? '';
  const isPoep = useMemo(() => source === 'POEP' || source === 'null', [source]);
  const [certificate, setCertificate] = useState(null);

  const fullName = `${name.toUpperCase()} ${lastName.toUpperCase()}`;
  const courseName = dataCourse?.title ?? '';
  const title = 'Certifica que ha participado en el curso';

  const pdfUrl = isPoep
    ? '/cerificate-pdf/CLA25OB00037_certificado_curso_poep.pdf'
    : '/cerificate-pdf/CLA24OB00209_certificado_curso.pdf';

  const getFormattedDate = useCallback(() => {
    const today = new Date();
    const currentDay = String(today.getDate()).padStart(2, '0');
    const currentMonth = String(today.getMonth() + 1).padStart(2, '0');
    const currentYear = today.getFullYear();

    const monthNames = [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];

    if (isPoep && certificate?.created && certificate?.register_user_course) {
      const [yearPoepFinish, monthPoepFinish, dayPoepFinish] = certificate.created.split("-");
      const [yearPoepInit, monthPoepInit, dayPoepInit] = certificate.register_user_course?.split("-");
      const monthIndexFinish = parseInt(monthPoepFinish, 10) - 1;
      const monthIndexInit = parseInt(monthPoepInit, 10) - 1;

      const monthTextFinish = monthNames[monthIndexFinish] || "";
      const monthTextInit = monthNames[monthIndexInit] || "";

      return `Realizado desde el ${dayPoepInit} de ${monthTextInit} de ${yearPoepInit} hasta el ${dayPoepFinish} de ${monthTextFinish} de ${yearPoepFinish}`;
    }

    return `${currentDay}/${currentMonth}/${currentYear}`;
  }, [isPoep, certificate]);

  const verifyCertificate = useCallback(async () => {
    try {
      const res = await GetCertificatesUserService(token, idUser);
      // const resCourse = await GetCoursesUserServices(token, idUser);

      const matchedCertificate = res.find(
        cert => cert.certificate?.certificate_title === courseName
      );

      if (matchedCertificate) {
        setIsActive(true);
        setCertificate(matchedCertificate); // Asegúrate de tener un estado llamado certificate
      } else {
        setIsActive(false);
        setCertificate(null); // Limpia el certificado si no se encuentra
      }

    } catch (error) {
      console.error('Error verificando certificado:', error);
    }
  }, [token, idUser, courseName, hasSentProgress]);

  const editPDF = async () => {
    try {
      const response = await fetch(pdfUrl);
      const existingPdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(existingPdfBytes);

      const [fontBold, fontRegular] = await Promise.all([
        pdfDoc.embedFont(StandardFonts.HelveticaBold),
        pdfDoc.embedFont(StandardFonts.Helvetica),
      ]);

      const page = pdfDoc.getPages()[0];
      const pageWidth = page.getSize().width;

      const color = {
        default: rgb(95 / 255, 126 / 255, 132 / 255),
        poep: rgb(20 / 255, 67 / 255, 120 / 255),
        name: rgb(0, 0, 0),
      };

      const fontSize = {
        name: isPoep ? 30 : 35,
        text: isPoep ? 12 : 20,
      };

      const getCenteredX = (text, font, size) =>
        (pageWidth - font.widthOfTextAtSize(text, size)) / 2;

      if (!isPoep) {
        page.drawText(title, {
          x: getCenteredX(title, fontRegular, fontSize.text),
          y: 410,
          size: fontSize.text,
          font: fontRegular,
          color: color.default,
        });

        page.drawText(courseName, {
          x: getCenteredX(courseName, fontRegular, fontSize.text),
          y: 390,
          size: fontSize.text,
          font: fontRegular,
          color: color.default,
        });
      }

      page.drawText(fullName, {
        x: getCenteredX(fullName, fontBold, fontSize.name),
        y: isPoep ? 380 : 310,
        size: fontSize.name,
        font: fontBold,
        color: color.name,
      });

      const formattedDate = getFormattedDate();
      page.drawText(formattedDate, {
        x: isPoep ? getCenteredX(formattedDate, fontRegular, fontSize.text) : 350,
        y: isPoep ? 230 : 175,
        size: fontSize.text,
        font: fontRegular,
        color: isPoep ? color.poep : color.default,
      });

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'certificado.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al editar el PDF:', error);
    }
  };

  useEffect(() => {
    verifyCertificate();
  }, [verifyCertificate]);

  return (
    <div
      className={`flex flex-col justify-center items-center ${isActive ? 'text-white' : 'text-gray-500'
        }`}
    >
      <button
        className={`flex items-center justify-center gap-1 rounded-md p-2 my-1 md:my-2 shadow ${isActive ? 'bg-green-400' : 'bg-gray-400/80 cursor-not-allowed'
          }`}
        disabled={!isActive}
        onClick={editPDF}
      >
        <FaDownload />
        Descargar Certificado
      </button>
    </div>
  );
};

export default CertificatesButtonComponent;
